import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  faPencil,
  faBan,
  faUsers,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FormField } from "../../../../interfaces/form-field.model";
import { MasterColourService } from "src/app/services/vehicle/master-colour.service";
import { NotifierService } from "angular-notifier";

@Component({
  selector: "app-master-colour-detail",
  templateUrl: "./master-colour-detail.component.html",
  styleUrls: ["./master-colour-detail.component.css"],
})
export class MasterColourDetailComponent {
  faPencil = faPencil;
  faBan = faBan;
  faPlus = faPlus;
  faUsers = faUsers;

  mode: string = "Add";
  lockUpdate: boolean = true;
  masterColourId: string = "0";

  availableInteriorColours: any[] = [];
  selectedInteriorColours: { value: string; display: string }[] = [];

  formFields: FormField[] = [
    {
      type: "text",
      id: "ModelId",
      label: "Model Id",
      lock: true,
      value: "",
      maxLength: 150,
    },
    {
      type: "select",
      id: "ModelGroup",
      label: "Model Group",
      required: true,
      value: undefined,
      options: [],
    },
    {
      type: "select",
      id: "Doors",
      label: "Doors",
      required: true,
      value: undefined,
      options: [
        {
          display: "2",
          value: "2",
        },
        {
          display: "3",
          value: "3",
        },
        {
          display: "4",
          value: "4",
        },
        {
          display: "5",
          value: "5",
        },
      ],
    },
    {
      type: "text",
      id: "Type",
      label: "Type",
      value: "",
      maxLength: 150,
    },
    {
      type: "text",
      id: "ExteriorColour",
      label: "Exterior Colour",
      required: true,
      value: "",
      maxLength: 150,
    },
    {
      type: "text",
      id: "Description",
      label: "Description",
      required: true,
      value: "",
      maxLength: 150,
    },
    {
      type: "text",
      id: "Remarks",
      label: "Remarks",
      value: "",
      maxLength: 150,
    },
    {
      type: "switch",
      id: "Display",
      label: "Display",
      value: false,
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private masterColourService: MasterColourService,
    private notifier: NotifierService,
  ) {
    masterColourService.GetModelGroup().subscribe({
      next: (response) => {
        this.getField("ModelGroup").options = response.body.map(
          (item: any) => ({
            display: item.Name,
            value: item.Value,
          }),
        );
        this.checkCanUpdate();
      },
      error: (error) => console.log(error),
    });
    this.route.params.subscribe((params) => {
      this.masterColourId = params["id"];
      if (this.masterColourId !== "0") {
        this.mode = "Edit";
        this.loadMasterColourDetail();
      }
    });
  }

  private loadMasterColourDetail(): void {
    this.masterColourService
      .getDetail(this.masterColourId)
      .subscribe({
        next: (response) => {
          const { ColourMasterId, ...data } = response.body;
          Object.entries(data).forEach(([key, value]) => {
            if (key === "InteriorColours") {
              this.selectedInteriorColours = String(value || "")
                .split("/")
                .filter(Boolean)
                .map(item => ({ value: item, display: item }));
            } else if (key === "Doors") {
              this.getField(key).value = String(value);
            } else {
              this.getField(key).value = value;
            }
          });
          this.changeInteriorColourOptions();
          this.checkCanUpdate();
        },
        error: (error: any) => {
          console.error("Error loading master colour detail:", error);
        },
      });
  }

  updateFields(event: any): void {
    this.formFields = event.fields;
    const changedField = event?.fields?.[event.updatedValue];
    if (changedField?.id === "ModelGroup") {
      this.selectedInteriorColours = [];
      this.changeInteriorColourOptions();
    }
    this.checkCanUpdate();
  }

  changeInteriorColourOptions(): void {
    const modelGroup = this.getField("ModelGroup").value;
    if (!modelGroup) {
      this.availableInteriorColours = [];
      return;
    }
    this.masterColourService
      .GetColourCode(modelGroup)
      .subscribe({
        next: (response) => {
          this.availableInteriorColours = response.body.map((colour: any) => ({
            value: colour.Value,
            display: colour.Value,
          }));
          this.checkCanUpdate();
        },
        error: (error) => console.log(error),
      });
  }

  SaveDetails(): void {
    if (!this.hasRequiredValues()) {
      this.notifier.notify("warning", "Please fill in all required fields.");
      return;
    }

    const data = {
      ColourMasterId: this.masterColourId,
      Description: this.getField("Description").value,
      Display: this.getField("Display").value,
      Doors: this.getField("Doors").value,
      InteriorColours: this.selectedInteriorColours
        .map((option) => option.value)
        .join("/"),
      InteriorColoursSelected: this.selectedInteriorColours
        .map((option) => ({ Value: option.value })),

      ExteriorColour: this.getField("ExteriorColour").value,
      ModelGroup: this.getField("ModelGroup").value,
      ModelId: this.getField("ModelId").value,
      Remarks: String(this.getField("Remarks").value ?? ""),
      Type: String(this.getField("Type").value ?? ""),
    };
    this.masterColourService.saveDetails(data).subscribe({
      next: (response) => {
        if (response?.code === 0) {
          this.notifier.notify(
            "success",
            response.message || "Master Colour saved successfully.",
          );
          this.router.navigate(["../../master-colour"], {
            relativeTo: this.route,
          });
        } else {
          this.notifier.notify(
            "error",
            response?.message || "Error saving Master Colour.",
          );
        }
      },

      error: (error) => {
        this.notifier.notify("error", "Error updating colour");
        console.log(error);
      },
    });
  }

  getField(id: string): any {
    const field = this.formFields.find((f) => f.id === id);
    return field;
  }

  onCancel(): void {
    this.router.navigate(["../../master-colour"], {
      relativeTo: this.route,
    });
  }

  checkCanUpdate(): void {
    this.lockUpdate = !this.hasRequiredValues();
  }

  onInteriorColoursChange(
    colours: { value: string; display: string }[],
  ): void {
    this.selectedInteriorColours = colours;
    this.checkCanUpdate();
  }

  private hasRequiredValues(): boolean {
    const requiredFieldsAreValid = this.formFields
      .filter((item) => item.required == true)
      .every((item) =>
        Array.isArray(item.value)
          ? item.value.length > 0
          : String(item.value ?? "").trim().length > 0,
      );
    return requiredFieldsAreValid && this.selectedInteriorColours.length > 0;
  }
}
